import React, { useState } from 'react';
import { Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { stockAPI } from '../services/api';
import LegalDisclaimer from '../components/LegalDisclaimer';
import StockHeaderCard from './resources/StockHeaderCard';
import PriceRangeCard from './resources/PriceRangeCard';
import OptionsFlowCard from './resources/OptionsFlowCard';
import { formatMarketCap } from './resources/formatters';

const Resources = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [stockData, setStockData] = useState(null);
  const [optionsFlow, setOptionsFlow] = useState(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      toast({ title: 'Enter a stock symbol', description: 'Please enter a valid stock ticker', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const quote = await stockAPI.getQuote(searchQuery.toUpperCase());
      if (!quote) {
        toast({ title: 'Stock not found', description: 'Please check the ticker symbol and try again' });
        setStockData(null);
        setOptionsFlow(null);
        return;
      }
      setStockData(quote);
      if (quote.marketCap) {
        try {
          const flowResponse = await stockAPI.getOptionsFlow(quote.symbol, quote.marketCap);
          setOptionsFlow(flowResponse);
        } catch (flowError) {
          console.warn('Error fetching options flow:', flowError);
          setOptionsFlow(null);
        }
      } else {
        setOptionsFlow(null);
      }
    } catch (error) {
      console.warn('Resources search failed:', error);
      toast({ title: 'Error', description: 'Failed to fetch stock data. Please try again.', variant: 'destructive' });
      setStockData(null);
      setOptionsFlow(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Stock Information</h1>
          <p className="text-gray-600">Get fast, accurate market data for any stock</p>
        </div>

        <Card className="mb-8">
          <CardContent className="pt-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="text"
                  placeholder="Enter stock ticker (e.g., AAPL, MSFT, GOOGL)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value.toUpperCase())}
                  className="pl-10 py-6 text-lg"
                  data-testid="resources-search-input"
                />
              </div>
              <Button
                type="submit"
                className="bg-purple-600 hover:bg-purple-700 px-8 py-6 text-lg"
                disabled={loading}
                data-testid="resources-search-btn"
              >
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {stockData && (
          <div className="space-y-6 animate-in fade-in duration-500">
            <StockHeaderCard stockData={stockData} />
            <PriceRangeCard stockData={stockData} />

            <Card>
              <CardHeader>
                <CardTitle>Additional Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    ['Symbol', stockData.symbol],
                    ['Company', stockData.name],
                    ['Market Cap', formatMarketCap(stockData.marketCap)],
                    ['P/E Ratio', stockData.peRatio ? stockData.peRatio.toFixed(2) : 'N/A'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">{k}</span>
                      <span className="font-semibold text-gray-900">{v}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <OptionsFlowCard optionsFlow={optionsFlow} stockSymbol={stockData.symbol} />

            <div className="flex justify-center">
              <Button
                onClick={() => (window.location.href = `/dashboard`)}
                className="bg-purple-600 hover:bg-purple-700 px-8 py-6 text-lg"
              >
                Get Full AI Analysis for {stockData.symbol}
              </Button>
            </div>
          </div>
        )}

        {!stockData && !loading && (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">📈</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Search for a Stock</h3>
            <p className="text-gray-600">
              Enter any stock ticker to view market data, price history, and key metrics
            </p>
          </div>
        )}

        <LegalDisclaimer variant="compact" />
      </div>
    </div>
  );
};

export default Resources;
